import { Chip, Button, Typography, Box } from '@mui/material'
import styled from '@emotion/styled/macro'
import theme from 'theme'

import { CARD_MIN_WIDTH, CARD_MIN_WIDTH_SMALL } from '../integrations.styles'

export const IntegrationCardStyled = styled(Box)<{ is_instance: string }>`
  min-width: ${CARD_MIN_WIDTH}px;
  color: ${theme.colors.foreground};
  transition: transform 0.2s;
  border-radius: ${theme.attributes.borderRadius.tertiary};
  border: 1px solid ${theme.colors.borderMedium};
  background: ${theme.colors.surfaceContainerBackground};
  height: ${({ is_instance: isInstance }) => (isInstance === 'true' ? '226px' : '240px')};

  &:hover {
    border: 1px solid ${theme.colors.dropdownBorder};
    background: ${theme.colors.devicesRowHoverBg};

    .categories-wrapper {
      .MuiChip-root {
        background: ${theme.colors.btnTertiaryBg};
        transition: none;
        animation: none;
      }
    }

    .card-header {
      border-color: ${theme.colors.dropdownBorder} !important;
    }

    .logo-container {
      background: ${theme.colors.btnTertiaryBg} !important;
    }

    .instance-error-wrapper {
      background: ${theme.colors.tooltipBg};
    }
  }

  @media screen and (min-width: 1200px) and (max-width: 1920px) {
    min-width: ${CARD_MIN_WIDTH_SMALL}px;
  }
`

export const CardHeader = styled(Box)`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: ${theme.spacing.small}px;
  gap: ${theme.spacing.lg}px;
  border-bottom: 1px solid ${theme.colors.borderMedium};

  .integration-name {
    color: ${theme.colors.menuItemText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.header};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.extraThick};
    line-height: 30px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .instance-name {
    color: ${theme.colors.dropdownLabel};

    html[data-theme='light'] & {
      color: ${theme.colors.textCaption};
    }

    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 130%;
    letter-spacing: 0.1px;
    flex-grow: 1;
  }

  .instance-description {
    color: ${theme.colors.devicesSecondaryText};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 20px;
    letter-spacing: 0.25px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding-bottom: ${theme.spacing.xSmall}px;
  }

  .instance-error-wrapper {
    display: flex;
    width: 24px;
    height: 24px;
    padding: ${theme.spacing.half}px;
    justify-content: center;
    align-items: center;
    gap: ${theme.spacing.sm}px;
    border-radius: ${theme.attributes.borderRadius.small};
    background: ${theme.colors.devicesRowHoverBg};
  }
`

export const CardContent = styled(Box)<{ hasInstances?: string }>`
  padding: 0;
  display: flex;
  flex-direction: column;

  .categories-wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: ${theme.spacing.xSmall}px;
    padding: ${({ hasInstances }) =>
      hasInstances && hasInstances === 'true'
        ? `${theme.spacing.small}px ${theme.spacing.small}px ${theme.spacing.xSmall}px`
        : `${theme.spacing.small}px ${theme.spacing.small}px ${theme.spacing.medium}px`};
  }

  .instance-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 ${theme.spacing.small}px;

    .MuiTypography-root {
      color: ${theme.colors.devicesSecondaryText};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.large};
      font-style: normal;
      font-weight: ${theme.typography.fontWeight.normal};
      line-height: 22px;

      span {
        color: ${theme.colors.menuItemText};
        font-family: ${theme.typography.fontFamily.primary};
        font-size: ${theme.typography.fontSize.large};
        font-style: normal;
        font-weight: ${theme.typography.fontWeight.bold};
        line-height: 20px;
      }
    }
  }
`

export const LogoContainer = styled(Box)`
  display: flex;
  width: 88px;
  height: 88px;
  padding: ${theme.spacing.xSmall}px;
  justify-content: center;
  align-items: center;
  gap: ${theme.spacing.sm}px;
  border-radius: ${theme.attributes.borderRadius.tertiary};
  background: ${theme.colors.devicesRowHoverBg};
  min-width: 88px;

  html[data-theme='light'] & {
    background: ${theme.colors.cardFillTertiary};
  }

  img {
    min-width: 56px;
    width: 56px;
    height: 56px;
    object-fit: cover;
    border-radius: ${theme.attributes.borderRadius.tertiary};
  }
`

export const NewChip = styled(Box)`
  display: flex;
  width: 72px;
  height: 28px;
  padding: 2px ${theme.spacing.xSmall}px;
  align-items: center;
  gap: ${theme.spacing.xSmall}px;
  border-radius: 6px;
  background: ${theme.colors.chipNewBg};
  color: ${theme.colors.white};
  text-align: center;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.medium};
  font-style: normal;
  font-weight: ${theme.typography.fontWeight.extraThick};
  line-height: 20px;
  letter-spacing: 0.1px;
  margin-top: ${theme.spacing.xsm}px;
`

export const CategoryChip = styled(Chip)`
  border-radius: 6px;
  background: ${theme.colors.devicesRowHoverBg};
  display: flex;
  align-items: center;
  padding: ${theme.spacing.half}px ${theme.spacing.small}px;
  gap: ${theme.spacing.xSmall}px;
  width: fit-content;
  /* Overrides the flex default of min-width: auto, which would otherwise hold each chip at its
   * full label width and push the row past the card edge. Lets long labels ellipsize instead. */
  min-width: 0;
  height: 28px;
  color: ${theme.colors.devicesSecondaryText};
  text-align: center;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.medium};
  font-style: normal;
  font-weight: ${theme.typography.fontWeight.extraThick};
  line-height: 20px;
  letter-spacing: 0.1px;

  html[data-theme='light'] & {
    background: ${theme.colors.cardFillTertiary};
  }
`

export const ViewInstancesButton = styled(Button)`
  display: flex;
  padding: ${theme.spacing.xSmall}px ${theme.spacing.small}px;
  justify-content: center;
  align-items: center;
  gap: ${theme.spacing.sm}px;
  align-self: stretch;
  border-radius: ${theme.attributes.borderRadius.mediumSmall};
  background: ${theme.colors.btnTertiaryBg};
  color: ${theme.colors.cta};
  text-align: center;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.large};
  font-style: normal;
  font-weight: ${theme.typography.fontWeight.bold};
  line-height: 20px;
  letter-spacing: 0.1px;
  margin: 0 ${theme.spacing.small}px ${theme.spacing.small}px;
  width: calc(100% - 32px);
  height: 36px;
  text-transform: none;

  &:hover {
    background-color: ${theme.colors.cta};
    color: ${theme.colors.surfaceContainerBackground};
  }
`

export const NoInstancesText = styled(Typography)`
  text-align: left;
  color: ${theme.colors.mutedMauve};
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.large};
  font-style: normal;
  font-weight: ${theme.typography.fontWeight.bold};
  line-height: 20px;
  letter-spacing: 0.1px;
  margin: ${theme.spacing.xSmall}px 0 0 ${theme.spacing.small}px;
`

export const AddIconWrapper = styled(Box)<{ isdisabled?: string }>`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 48px;
  width: 48px;
  height: 48px;
  padding: ${theme.spacing.xSmall}px;
  gap: ${theme.spacing.sm}px;
  border-radius: ${theme.attributes.borderRadius.tertiary};
  background: ${theme.colors.btnTertiaryBg};

  svg:not(.edit-icon) path {
    fill: ${theme.colors.cta};
  }

  ${({ isdisabled }) =>
    isdisabled === 'true'
      ? `
    /* Background stays put and only the glyph mutes — btnSecondaryDisabledBg is
     * transparent in light mode, which would drop the chip entirely. */
    cursor: not-allowed;

    svg:not(.edit-icon) path {
      fill: ${theme.colors.btnSecondaryDisabledText};
    }

    .edit-icon path {
      fill: none;
      stroke: ${theme.colors.btnSecondaryDisabledText};
    }
  `
      : `
    &:hover {
      background: ${theme.colors.cta};
      cursor: pointer;

      html[data-theme='light'] & {
        background: #9e1068;
      }

      svg path {
        fill: ${theme.colors.surfaceContainerBackground};
      }

      .edit-icon path {
        fill: none;
        stroke: ${theme.colors.surfaceContainerBackground};
      }
    }
  `};
`
